import {
  BadgeDollarSign,
  Briefcase,
  Clock,
  FileSignature,
  FileText,
  type LucideIcon,
  Paperclip,
  Receipt,
  StickyNote,
  Trash2,
  UserPlus,
  UserRound,
  UserRoundPen,
  Wallet,
} from 'lucide-react'

/** Exact event → icon (covers the client-timeline events we surface). */
const ACTIVITY_ICONS: Record<string, LucideIcon> = {
  'client.created': UserPlus,
  'client.updated': UserRoundPen,
  'client.deleted': Trash2,
  'client.note_added': StickyNote,
  'client.contact_created': UserPlus,
  'client.contact_updated': UserRoundPen,
  'client.contact_deleted': Trash2,
  'client.credit_status_changed': BadgeDollarSign,
  'document.uploaded': Paperclip,
  'document.linked': Paperclip,
  'document.copied': Paperclip,
  'document.deleted': Trash2,
  'quote.created': FileText,
  'quote.updated': FileText,
  'quote.sent': FileText,
  'quote.accepted': FileText,
  'invoice.created': Receipt,
  'invoice.updated': Receipt,
  'invoice.sent': Receipt,
  'invoice.paid': Wallet,
  'payment.received': Wallet,
  'payment.created': Wallet,
  'payment.refunded': Wallet,
  'contract.created': FileSignature,
  'contract.updated': FileSignature,
  'contract.signed': FileSignature,
  'contract.activated': FileSignature,
  'job.created': Briefcase,
  'job.updated': Briefcase,
  'job.assigned': Briefcase,
  'job.completed': Briefcase,
  'job.closed': Briefcase,
}

/** Domain prefix fallback when an exact event is not listed. */
const ACTIVITY_DOMAIN_ICONS: Record<string, LucideIcon> = {
  client: UserRound,
  document: Paperclip,
  quote: FileText,
  invoice: Receipt,
  payment: Wallet,
  credit_note: Receipt,
  contract: FileSignature,
  job: Briefcase,
}

/** Visual tone for activity rows (Figma 2889:57134). Brand is the default. */
export type ActivityIconTone = 'success' | 'info' | 'brand'

const ACTIVITY_ICON_TONE_STYLES: Record<
  ActivityIconTone,
  { box: string; icon: string }
> = {
  success: { box: 'bg-[#d1fae5]', icon: 'text-[#059669]' },
  info: { box: 'bg-[#e0f2fe]', icon: 'text-[#0284c7]' },
  brand: { box: 'bg-[#fff1ea]', icon: 'text-[#ff5a1f]' },
}

/** Map activity event types to Figma icon chip tones. */
export function activityIconTone(eventType?: string | null): ActivityIconTone {
  const type = (eventType ?? '').toLowerCase()
  if (
    type.includes('completed') ||
    type.includes('approved') ||
    type.includes('paid') ||
    type.includes('signed') ||
    type.includes('accepted') ||
    type.includes('activated') ||
    type.includes('closed') ||
    type.includes('received') ||
    type.includes('renewed')
  ) {
    return 'success'
  }
  if (
    type.includes('arrived') ||
    type.includes('assigned') ||
    type.includes('started') ||
    type.includes('reassigned') ||
    type.includes('created')
  ) {
    return 'info'
  }
  return 'brand'
}

export function activityIconBoxClass(eventType?: string | null): string {
  return ACTIVITY_ICON_TONE_STYLES[activityIconTone(eventType)].box
}

export function activityIconClass(eventType?: string | null): string {
  return ACTIVITY_ICON_TONE_STYLES[activityIconTone(eventType)].icon
}

/** Resolve a Lucide icon for an activity-log `event_type` (e.g. `client.updated`). */
export function activityIcon(eventType?: string | null): LucideIcon {
  if (!eventType) return Clock
  if (ACTIVITY_ICONS[eventType]) return ACTIVITY_ICONS[eventType]
  const domain = eventType.split('.')[0]
  return ACTIVITY_DOMAIN_ICONS[domain] ?? Clock
}
